/**
 * Backlog バグチケット起票の共有標準・設定。esa #4070「バグチケット起票ガイドライン」を単一 source として
 * 符号化し、MCP（customer-success の investigate.backlog_check）と PIL（ops 依頼からのバグ起票）で同じ
 * 記載標準・必須フィールド・重大度基準を参照する。
 * @graph-stack ops-core
 * @graph-domain Operations
 * @graph-business Backlog バグチケットの記載標準（タイトル/本文順/テンプレ）・起票先設定（プロジェクト/種別/必須カスタムフィールド）・重大度（インシデントレベル/バグ緊急度）判断材料を MCP と PIL で共有する source
 * @graph-connects none
 */
import { OPS_BUG_ISSUE_TYPE_IDS } from './backlog.js';
import { OPS_BUG_DISCOVERY_CHANNEL_FIELD, OPS_BUG_DISCOVERY_CHANNEL_FROM_OPS, OPS_BUG_RESPONSIBLE_FIELDS, } from './bug-ticket-fields.js';
/** バグチケット起票の記載標準（本体は cortex 側の本 standard、esa #4070 は任意の追加チェック）。investigate.backlog_check で agent に提示する。 @graph-connects none */
export const OPS_BUG_TICKET_STANDARD = {
    guidelineUrl: 'https://air-closet.esa.io/posts/4070',
    checkAgainst: '本 standard が必須基準。esa #4070（guidelineUrl, 全社ガイドライン）との突き合わせは nice-to-have — esa MCP がすぐ応答すれば追加確認に使い、認証/権限/バリデーション失敗や遅延時はリトライせずスキップして本 standard で進める（esa 失敗で起票をブロックしない）。',
    title: 'タイトルは業務影響の要約（誰の/何が/どうなったか）。テーブル名・関数名・サービス名・コード条件を主語にしない。[TEST]/【AC・ops◯】等の管理プレフィクス・WIP/在中 等の語は不可。推奨形: 【サービス略称】<業務語の対象>が<業務語の事象>（<影響範囲>）',
    bodyOrder: [
        '影響（必須・冒頭）',
        '発生範囲',
        '発生事象/再現手順',
        '検知の経緯',
        '原因（要約→技術詳細）',
        '暫定対応',
        '恒久対応',
        '再発防止策',
        '参考リンク',
    ],
    bodyRules: {
        影響: 'ビジネス/ユーザー視点で3行以内・本文冒頭に必須。誰に/何が起きたか(業務語)/期間/金銭・CS影響。エンジニア用語禁止',
        発生範囲: '対象ユーザー数・SKU数・件数/期間/環境を数値で。「一部の」「多数の」だけの曖昧表現は避け、算出方法も書く',
        原因: '日本語要約（引き金・直接原因・なぜ気づけなかったか）を先に3〜5行。コード/permalink/SQL は「### 技術詳細」に後置し、本文冒頭をコードブロックで始めない',
        再発防止策: 'lint ルール/アラート/モニタリング/ガイドライン更新 等の仕組みで書く。「気をつける」「レビューを厚くする」は不可',
    },
    antiPatterns: [
        '本文冒頭をコードブロックで始める',
        '影響セクションを省略する',
        'タイトルが技術語のみで業務動詞が無い',
        '原因を「調査中」のまま放置する（現時点の仮説と次の調査ステップを書く）',
        'permalink を貼って「詳細はこちら」で済ませる',
        '本文を英語のみで書く（バイリンガルなら日本語を先頭に）',
        'オペ No だけ書いて ops シート該当行 / #daily_operation スレッドのリンクを省略する',
    ],
    references: '参考リンクには オペ No だけでなく実リンクを貼る。(1) ops シート該当行 = オペ No で特定してリンク、(2) Slack = #daily_operation を op # / サービス名で検索し該当スレッドの permalink を取得。どちらも agent が解決する（tool はリンクを提供しない）。',
    template: [
        '## 影響',
        '- 対象: <ユーザーセグメント / SKU / 機能 を業務語で>',
        '- 事象: <業務語で何が起きたか 1〜2 行>',
        '- 期間: <YYYY-MM-DD HH:MM 〜 YYYY-MM-DD HH:MM>',
        '- 件数 / 金額: <概算と算出方法>',
        '- CS / 業務影響: <問い合わせ件数 / 手動リカバリ工数など>',
        '',
        '## 発生範囲',
        '- 対象サービス / 対象環境（本番・staging）/ 対象セグメント',
        '',
        '## 発生事象 / 再現手順',
        '1. ...',
        '観測された画面・データ: <スクショ / ログ抜粋 / SQL 結果>',
        '',
        '## 検知の経緯',
        '- 検知日時 / 検知方法（CS問い合わせ・アラート・内部監視）/ 検知が遅れた理由',
        '',
        '## 原因（要約）',
        '- 引き金: / 直接原因: / 検知遅延理由:',
        '',
        '### 技術詳細',
        '<コード抜粋・permalink(SHA固定)・スタックトレース・SQL など>',
        '',
        '## 暫定対応',
        '- <YYYY-MM-DD HH:MM、誰が、何をしたか>',
        '',
        '## 恒久対応',
        '- PR: <#番号> <一行サマリ>',
        '',
        '## 再発防止策',
        '- [ ] lint / アラート / モニタリング / ガイドライン更新 等（仕組みで書く）',
        '',
        '## 参考リンク',
        '- ops 依頼シートの該当行（オペ No で特定）',
        '- #daily_operation の該当スレッド（op # / サービス名で検索して permalink を取得）',
        '- Grafana / NewRelic / 関連 Backlog',
    ].join('\n'),
};
/**
 * 既存バグ重複チェック用の Backlog 参照。investigate は「新規バグ起票前に既存バグを検索せよ」と
 * agent に指示するためにこれを返す。**サーバは Backlog creds を持たず**、実検索は agent が
 * Backlog MCP で行う（detailed-design §7/§274）。IDs は AIRCLOSET プロジェクトのバグ issue types。
 * @graph-connects none
 */
export const OPS_BACKLOG_BUG = {
    projectKey: 'AIRCLOSET',
    projectId: 53826,
    /** 案件起因バグ (721649) / 潜在バグ (721648)。backlog.ts の単一 source を再利用（drift 防止）。 */
    issueTypeIds: OPS_BUG_ISSUE_TYPE_IDS,
    /**
     * 新規バグ起票時に必須のカスタムフィールド。add_issue で customField_<id> に option id を渡す（未設定は reject）。
     * 先頭はバグ緊急度（`requiredCustomFields[0]` で参照される）。`recommendedOptionId` は ops 依頼からの起票での既定値。
     */
    requiredCustomFields: [
        {
            id: 220923,
            name: 'バグ緊急度',
            apiKey: 'customField_220923',
            options: [
                { id: 1, label: '低 - low' },
                { id: 2, label: '中 - medium' },
                { id: 3, label: '高 - high' },
                { id: 4, label: '最優先 - super high' },
                { id: 5, label: '致命的 - fatal' },
            ],
        },
        {
            id: OPS_BUG_DISCOVERY_CHANNEL_FIELD.id,
            name: '発見の経路（202610新運用）',
            apiKey: `customField_${String(OPS_BUG_DISCOVERY_CHANNEL_FIELD.id)}`,
            options: OPS_BUG_DISCOVERY_CHANNEL_FIELD.options,
            recommendedOptionId: OPS_BUG_DISCOVERY_CHANNEL_FROM_OPS,
        },
        // Responsible 組織 4 種も Backlog が起票時に必須とする（未設定は「Responsible … は必須です」で 400）。
        ...[
            ['Responsible Div', OPS_BUG_RESPONSIBLE_FIELDS.div],
            ['Responsible Group', OPS_BUG_RESPONSIBLE_FIELDS.group],
            ['Responsible Team', OPS_BUG_RESPONSIBLE_FIELDS.team],
            ['Business', OPS_BUG_RESPONSIBLE_FIELDS.business],
        ].map(([name, field]) => ({
            id: field.id,
            name,
            apiKey: `customField_${String(field.id)}`,
            options: field.options,
        })),
    ],
};
/** バグ起票時の インシデントレベル / バグ緊急度 を根拠付きで推奨するための判断材料（PDG 基準 + 暫定対応ルール）。 @graph-connects none */
export const OPS_SEVERITY_GUIDANCE = {
    incidentLevel: {
        field: 'インシデントレベル',
        required: false,
        sourceUrl: 'https://docs.google.com/spreadsheets/d/1fAyUlomJZB4C6JlVG0-R4Br5cJ3y3qgXD_3o-SzuJgg/edit#gid=1194828640',
        criteria: [
            {
                level: 'レベル1（小）',
                impact: '10名未満 / 5万円未満',
                examples: '対象機能（決済・プラン変更・退会・クーポン・オプション・今回アイテム・会員登録・ログイン・誤配信/誤PUSH）の会員0.1%未満の機能不全 / 出荷・返却停止が当日量の10%未満 / データ流出（スタイリング<1000・取引<10・在庫<1000件）',
            },
            {
                level: 'レベル2（中）',
                impact: '50名未満 / 50万円未満',
                examples: '同対象機能の会員0.1%以上の機能不全 / 出荷・返却停止が当日量の10%以上 / データ流出が上記件数以上',
            },
            {
                level: 'レベル3（大）',
                impact: '50名以上 / 50万円以上 / 上場影響あり',
                examples: '同対象機能の会員0.5%以上の機能不全 / 出荷・返却停止が当日量の50%以上 / 個人情報流出・システムジャック',
            },
        ],
    },
    bugUrgency: {
        field: 'バグ緊急度',
        required: true,
        fieldRef: 'customField_220923',
        rule: '重大度（インシデントレベル相当の影響度）を起点に緊急度を選ぶ。暫定対応（ワークアラウンド/緩和策）が既にあれば恒久対応を待てるため1段下げる。暫定対応が無く実害が継続するなら上げる。',
    },
    discipline: '推奨であって結論ではない。investigate が集めた実データ（影響会員数・金額・対象機能・データ種別・暫定対応の有無）から根拠を示して推奨し、最終的な レベル / 緊急度 はエンジニアが確定する。',
};
/**
 * 関連案件チケットキーのカスタムフィールド ID（customField_109226、単一行テキスト）。案件起因バグ起票時に
 * 起因案件のキーをここへ入れると、backlog-webhook が起因案件の開発リーダー（PL）を自票へ補完する起点になる。
 * @graph-connects none
 */
export const OPS_RELATED_ISSUE_KEY_FIELD_ID = 109226;
//# sourceMappingURL=bug-ticket.js.map